// src/pages/UserMaps.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import logo from "../assets/logo128x128.png";
import MapCard from "../components/MapCard";
import "../Homepage.css";

function capitalise(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getDisplayName(map) {
  if (
    !map.name ||
    map.name.trim() === "" ||
    map.name.trim().toLowerCase() === map.game_name?.trim().toLowerCase()
  ) {
    return { main: capitalise(map.game_name), game: null };
  }
  return { main: map.name, game: capitalise(map.game_name) };
}

function UserMaps() {
  const { id } = useParams(); // /user/id/:id/maps
  const navigate = useNavigate();
  const [user, setUser] = useState(null); // logged in user
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [votesData, setVotesData] = useState({});
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteError, setDeleteError] = useState(null);
  const [owner, setOwner] = useState(null); // owner user object

  useEffect(() => {
    setLoading(true);
    setDeleteError(null);
    if (!id) {
      setOwner(null);
      setMaps([]);
      setLoading(false);
      return;
    }

    fetch("/api/me", { credentials: "include" })
      .then(res => res.json())
      .then(me => setUser(me))
      .catch(() => setUser(null));

    fetch(`/api/user/by-id/${id}`)
      .then(res => {
        if (!res.ok) throw new Error("User not found");
        return res.json();
      })
      .then(ownerUser => setOwner(ownerUser))
      .catch(() => setOwner(null));

    fetch(`/api/user/id/${id}/maps`, { credentials: "include" })
      .then(res => {
        if (!res.ok) throw new Error("User not found");
        return res.json();
      })
      .then(mapsData => setMaps(mapsData))
      .catch(() => setMaps([]))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!maps.length) return;
    const loadVotes = async () => {
      const newVotesData = {};
      for (let map of maps) {
        try {
          const res = await fetch(`/api/map-votes/${map.id}/0`);
          const data = await res.json();
          newVotesData[map.id] = data;
        } catch (e) {
          newVotesData[map.id] = { total_votes: map.votes || 0, user_vote: 0 };
        }
      }
      setVotesData(newVotesData);
    };
    loadVotes();
  }, [maps]);

  // --- Enhanced: allow delete for user 1, founder, or admin ---
  const canDeleteMap = (map) => {
    if (!user) return false;
    return (
      user.id === 1 ||
      user.role === "admin" ||
      user.role === "founder" ||
      user.id === map.uploader_id
    );
  };

  // Only show parent maps (no parent_map_id)
  const filteredMaps = maps
    .filter(map => !map.parent_map_id)
    .filter(map =>
      (map.name && map.name.toLowerCase().includes(search.toLowerCase())) ||
      (map.game_name && map.game_name.toLowerCase().includes(search.toLowerCase()))
    );

  const handleDelete = async (map) => {
    if (!canDeleteMap(map)) {
      alert("You do not have permission to delete this map.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete "${map.name || map.game_name}"?\nThis will delete all POIs for this map too!`
      )
    ) {
      return;
    }

    setDeleteLoading(l => ({ ...l, [map.id]: true }));
    setDeleteError(null);
    try {
      const res = await fetch(`/api/upload-map/${map.id}`, {
        method: "DELETE",
        credentials: "include"
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to delete map.");
      setMaps(maps => maps.filter(m => m.id !== map.id));
    } catch (e) {
      setDeleteError(e.message);
    }
    setDeleteLoading(l => ({ ...l, [map.id]: false }));
  };

  return (
    <div className="homepage">
      <header
        className="homepage-header"
        style={{ display: "flex", alignItems: "center", gap: "1rem" }}
      >
        <img
          src={logo}
          alt="Point Atlas Logo"
          style={{ width: 40, height: 40, objectFit: "contain", cursor: "pointer" }}
          onClick={() => navigate("/")}
        />
        <h1 className="brand" style={{ margin: 0 }}>
          {owner ? `Maps by @${owner.username || owner.id}` : "User Maps"}
        </h1>
        <input
          className="search-bar"
          type="text"
          placeholder="Search maps..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
      </header>

      <div className="maps-grid">
        {loading ? (
          <p style={{ color: "#fff" }}>Loading maps...</p>
        ) : filteredMaps.length === 0 ? (
          <p style={{ color: "#fff" }}>
            {owner
              ? "This user hasn't uploaded any maps yet."
              : "User not found."}
          </p>
        ) : (
          filteredMaps.map(map => (
            <div key={map.id} style={{ position: "relative" }}>
              <MapCard
                map={map}
                showVotes={true}
                votes={votesData[map.id]?.total_votes}
                userVote={votesData[map.id]?.user_vote}
              />
              {canDeleteMap(map) && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
                  <button
                    className="map-modal-button"
                    style={{ fontSize: 14, padding: '2px 10px', background: '#b33', color: 'white', borderRadius: 4 }}
                    onClick={() => handleDelete(map)}
                    disabled={deleteLoading[map.id]}
                  >
                    {deleteLoading[map.id] ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      {deleteError && <div style={{ color: 'red', textAlign: 'center', marginTop: 12 }}>{deleteError}</div>}
    </div>
  );
}

export default UserMaps;
